// 1. React imports
import { useEffect, useRef } from 'react';

// 3. Internal components
import { Progress } from '~/components/ui/progress';

/**
 * Props for WizardProgress component.
 */
export interface WizardProgressProps {
  currentStep: number;
  totalSteps?: number;
}

/**
 * Step titles for context display.
 */
const STEP_TITLES: Record<number, string> = {
  1: 'Welcome to DeadDrop',
  2: 'Create Your First Topic',
  3: 'Set Up Your Client',
  4: 'Connect Telegram Bot',
  5: 'Send a Message From Telegram',
  6: 'Retrieve Your Message',
};

/**
 * Step indicator for onboarding wizard.
 *
 * Displays a visible progress bar plus the current step number and title.
 * Moves focus to the step heading whenever currentStep changes, so screen
 * reader and keyboard users land on the new step's content immediately.
 *
 * Per §TS.3.1: Function declaration for named export.
 * Per §TS.3.2: Props interface with descriptive naming.
 *
 * @example
 * ```tsx
 * <WizardProgress currentStep={2} totalSteps={6} />
 * // Renders: "Step 2 of 6: Create Your First Topic"
 * ```
 */
export function WizardProgress({ currentStep, totalSteps = 6 }: WizardProgressProps) {
  const stepTitle = STEP_TITLES[currentStep] || '';
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Move focus to the step heading on every step change so keyboard and
  // screen reader users are placed at the start of the new step's content.
  useEffect(() => {
    headingRef.current?.focus();
  }, [currentStep]);

  return (
    <div className="mb-6">
      <p className="text-sm text-muted-foreground">
        Step {currentStep} of {totalSteps}
      </p>
      <Progress
        value={currentStep}
        max={totalSteps}
        aria-label={`Step ${currentStep} of ${totalSteps}`}
        className="mt-2 mb-3"
      />
      {stepTitle && (
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="mt-1 font-heading text-4xl font-bold tracking-tight outline-none"
        >
          {stepTitle}
        </h1>
      )}
    </div>
  );
}
